/* eslint-disable @nx/enforce-module-boundaries */
import {
  computed,
  inject,
  Injectable,
  Injector,
  runInInjectionContext,
  Signal,
  signal,
} from '@angular/core';
import {
  ProductsWMSFilterModel,
  ProductWMSListModel,
  ProductWMSModel,
} from '@kanzi-apes/kanzi-models';
import {
  injectQuery,
  injectQueryClient,
  keepPreviousData,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';
import { ProductLineWmsService } from './product-list-wms.service';
import { ProductListQueryKeys } from './product-list-key.factory';

export interface IProductsSelected extends ProductWMSModel {
  isSelected: boolean;
  amount: number;
}

@Injectable()
export class ProductListQuery {
  private injector = inject(Injector);

  private readonly queryClient = injectQueryClient();
  private readonly productLineWmsService = inject(ProductLineWmsService);

  // ✅ Signals privados para estado interno
  private readonly _filters = signal<ProductsWMSFilterModel | null>(null);
  private readonly _barcodeFilter = signal<string>('');
  private readonly _productsSelected = signal<IProductsSelected[]>([]);

  // ✅ Computed signals públicos de solo lectura
  public readonly filters = computed(() => this._filters());
  public readonly productsSelected = computed(() => this._productsSelected());

  /**
   * Map de productos seleccionados para búsquedas O(1)
   */
  private readonly productsSelectedMap = computed(() => {
    const map = new Map<string, IProductsSelected>();
    this._productsSelected().forEach((product) => {
      if (product.id) {
        map.set(product.id, product);
      }
    });
    return map;
  });

  /**
   * Query principal de productos con filtros aplicados
   * Se ejecuta solo cuando los filtros cambian
   */
  public readonly productsQuery = injectQuery(() => ({
    queryKey: ProductListQueryKeys.all(this._filters()),
    queryFn: async () =>
      lastValueFrom(this.productLineWmsService.getProductList(this._filters())),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos (antes cacheTime)
    retry: 2,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    select: (payload) => {
      const selectedMap = this.productsSelectedMap();
      return payload.results.map((item) => {
        const selected = selectedMap.get(item.id || '');
        return {
          ...item,
          isSelected: !!selected,
          amount: selected?.amount ?? 0,
        };
      });
    },
  }));

  /**
   * Actualiza los filtros de búsqueda de productos
   * Esto disparará automáticamente la query si los filtros cambian
   */
  public updateFilters(filters: ProductsWMSFilterModel | null): void {
    this._filters.set(filters);
  }

  /**
   * Busca productos por código de barras
   * Retorna una Promise para uso con async/await
   */
  public async searchProductByBarcode(
    barcode: string
  ): Promise<IProductsSelected[]> {
    if (!barcode.trim()) return [];

    try {
      const result = await lastValueFrom(
        this.productLineWmsService.getProductList({
          barcode__contains: barcode,
        })
      );

      const selectedMap = this.productsSelectedMap();
      return result.results.map((item) => {
        const selected = selectedMap.get(item.id || '');
        return {
          ...item,
          isSelected: !!selected,
          amount: selected?.amount ?? 0,
        };
      });
    } catch (error) {
      console.error('Error searching product by barcode:', error);
      return [];
    }
  }

  /**
   * Guarda los productos seleccionados
   * Reemplaza completamente la lista de productos seleccionados
   */
  public saveProductsSelected(products: IProductsSelected[]): void {
    this._productsSelected.set([...products]);
    this.invalidateQueries();
  }

  /**
   * Actualiza o agrega productos a la lista de seleccionados
   * Mantiene la inmutabilidad usando Map
   */
  public updateProductsSelected(products: IProductsSelected[]): void {
    const currentProducts = [...this._productsSelected()];
    const productsMap = new Map(
      currentProducts.map((p) => [p.reference_id, p])
    );

    products.forEach((product) => {
      if (!product.reference_id) return;

      const existingProduct = productsMap.get(product.reference_id);

      if (existingProduct) {
        // Actualizar producto existente
        const updatedAmount =
          existingProduct.reception_type === 'GENERAL'
            ? Math.trunc(existingProduct.amount + product.amount)
            : existingProduct.amount + product.amount;

        productsMap.set(product.reference_id, {
          ...existingProduct,
          amount: updatedAmount,
          isSelected: true,
        });
      } else {
        // Agregar nuevo producto
        const newAmount =
          product.reception_type === 'GENERAL'
            ? Math.trunc(product.amount)
            : product.amount;

        productsMap.set(product.reference_id, {
          ...product,
          amount: newAmount,
          isSelected: true,
        });
      }
    });

    this._productsSelected.set(Array.from(productsMap.values()));
    this.invalidateQueries();
  }

  /**
   * Elimina un producto de la lista de seleccionados
   */
  public removeProductSelected(referenceId: string): void {
    const currentProducts = this._productsSelected();
    const filtered = currentProducts.filter(
      (p) => p.reference_id !== referenceId
    );
    this._productsSelected.set(filtered);
    this.invalidateQueries();
  }

  /**
   * Limpia todos los productos seleccionados
   */
  public clearProductsSelected(): void {
    this._productsSelected.set([]);
    this.invalidateQueries();
  }

  /**
   * Invalida las queries para forzar refetch
   */
  private invalidateQueries(): void {
    this.queryClient.invalidateQueries({
      queryKey: ['wms', 'products', 'list'],
    });
  }

  /**
   * Método público para invalidar queries manualmente
   */
  public onInvalidQuery(): void {
    this.invalidateQueries();
  }

  /**
   * Actualiza el filtro de código de barras
   */
  public updateBarcodeFilter(barcode: string): void {
    this._barcodeFilter.set(barcode);
  }

  public productByBarcode = (
    filterSignal: Signal<ProductsWMSFilterModel | null>
  ) =>
    runInInjectionContext(this.injector, () =>
      injectQuery(() => ({
        queryKey: ['wms', 'products', 'list-by-barcode', filterSignal()],
        queryFn: () =>
          lastValueFrom(
            this.productLineWmsService.getProductList(
              filterSignal() as ProductsWMSFilterModel
            )
          ),
        retry: 2,
        select: (payload) =>
          payload.results.map((item) => ({
            ...item,
            isSelected: this.searchProductSelected(item),
            amount:
              this.productsSelected()?.find((p) => p.id === item.id)?.amount ??
              0,
          })),
        enabled: filterSignal() !== null,
      }))
    );

  private searchProductSelected(payload: ProductWMSModel): boolean {
    return !!this.productsSelected()?.find((item) => item.id === payload.id);
  }

  public onSaveProductSelected(products: IProductsSelected[]) {
    this._productsSelected.set([...products]);
  }

  public async onUpdateProductsSelected(products: IProductsSelected[]) {
    const currentProducts = [...this._productsSelected()];
    const productsMap = new Map(
      currentProducts.map((p) => [p.reference_id, p])
    );
    products.forEach((product) => {
      if (!product.id) return;

      const existingProduct = productsMap.get(product.reference_id);

      if (existingProduct) {
        const updatedAmount =
          existingProduct.reception_type === 'GENERAL'
            ? Math.trunc(existingProduct.amount + product.amount)
            : existingProduct.amount + product.amount;
        productsMap.set(product.reference_id, {
          ...existingProduct,
          amount: updatedAmount,
          isSelected: true,
        });
      } else {
        const newAmount =
          product.reception_type === 'GENERAL'
            ? Math.trunc(product.amount)
            : product.amount;
        productsMap.set(product.reference_id, {
          ...product,
          amount: newAmount,
          isSelected: true,
        });
      }
    });

    this._productsSelected.set(Array.from(productsMap.values()));
  }
}
